import { expandBaseWebBorder } from "@gadgetinc/widgets";
import { useStyletron } from "baseui";
import React from "react";
import { FieldType } from "state-trees/src/type-system/FieldType";
import { FieldTypeTag } from "web/src/components/edit/shared/FieldTypeTag";
import { Paragraph } from "web/src/components/markdown/Typography";
import { InlineCode } from "./index";

const gadgetFieldTypes = [
  {
    type: FieldType.ID,
    graphQLType: <InlineCode>GadgetGraphQLID</InlineCode>,
    notes: "Custom GraphQL scalar representing Gadget ID values as strings.",
  },
  { type: FieldType.String, graphQLType: <InlineCode>GraphQLString</InlineCode> },
  { type: FieldType.Boolean, graphQLType: <InlineCode>GraphQLBoolean</InlineCode> },
  {
    type: FieldType.URL,
    graphQLType: (
      <>
        <InlineCode>GraphQLURL</InlineCode> or <InlineCode>GraphQLString</InlineCode>
      </>
    ),
    notes: "Returns a custom GraphQLURL scalar if URL validation is turned on, and a string otherwise",
  },
  {
    type: FieldType.Email,
    graphQLType: (
      <>
        <InlineCode>GraphQLEmailAddress</InlineCode> or <InlineCode>GraphQLString</InlineCode>
      </>
    ),
    notes: "Returns a custom GraphQLEmailAddress scalar if URL validation is turned on, and a string otherwise",
  },
  { type: FieldType.Enum, graphQLType: <InlineCode>[GraphQLString!]</InlineCode>, notes: "Returns a list of strings" },
  {
    type: FieldType.RichText,
    graphQLType: <InlineCode>GraphQLRichText</InlineCode>,
    notes:
      "Returns a custom object representing the rich text, with html, markdown and truncatedHTML fields for accessing the content in different ways",
  },
  {
    type: FieldType.DateTime,
    graphQLType: (
      <>
        <InlineCode>GraphQLDate</InlineCode> or <InlineCode>GraphQLDateTime</InlineCode>
      </>
    ),
    notes: "Returns a full GraphQLDateTime if the Include Time option is on, and a GraphQLDate otherwise",
  },
  {
    type: FieldType.Number,
    graphQLType: (
      <>
        <InlineCode>GraphQLInt</InlineCode> or <InlineCode>GraphQLFloat</InlineCode>
      </>
    ),
    notes: "Returns a GraphQLFloat if the allowed number of decimals is unset or greater than 0, and a GraphQLInt otherwise",
  },
  {
    type: FieldType.JSON,
    graphQLType: <InlineCode>GraphQLJSON</InlineCode>,
    notes: "JSON fields are used to store valid JSON. Valid values can be objects, arrays, or scalars including strings and numbers.",
  },
  {
    type: FieldType.Any,
    graphQLType: <InlineCode>GraphQLJSON</InlineCode>,
    notes: "Deprecated. Any fields act the same as JSON fields.",
  },
  {
    type: FieldType.BelongsTo,
    graphQLType: <InlineCode>{"<related object type>"}</InlineCode>,
    notes: "Returns the GraphQL type for the related model.",
  },
  {
    type: FieldType.HasOne,
    graphQLType: <InlineCode>{"<related object type>"}</InlineCode>,
    notes: "Returns the GraphQL type for the related model.",
  },
  {
    type: FieldType.HasMany,
    graphQLType: <InlineCode>{"<related object type>Connection"}</InlineCode>,
    notes: (
      <>
        Returns a <a href="https://graphql.org/learn/pagination/#complete-connection-model">Relay style Connection</a> interface for
        accessing the list of related records. The connection has the pageInfo and edges properties.
      </>
    ),
  },
  {
    type: FieldType.HasManyThrough,
    graphQLType: <InlineCode>{"<related object type>Connection"}</InlineCode>,
    notes: (
      <>
        Returns a <a href="https://graphql.org/learn/pagination/#complete-connection-model">Relay style Connection</a> interface for
        accessing the list of related records. The connection has the pageInfo and edges properties.
      </>
    ),
  },
];

export const GraphQLTypeTable = (props?: React.ReactNode) => {
  const [css, $theme] = useStyletron();
  return gadgetFieldTypes.map((field, index) => (
    <div
      key={index}
      className={css({
        ...expandBaseWebBorder($theme.borders.border200),
        borderBottomWidth: "0px",
        borderLeftWidth: "0px",
        borderRightWidth: "0px",
        paddingTop: $theme.sizing.scale600,
      })}
    >
      <FieldTypeTag fieldType={field.type}>{field.type}</FieldTypeTag>
      <Paragraph
        className={css({
          marginTop: $theme.sizing.scale100,
          paddingBottom: $theme.sizing.scale300,
          color: $theme.colors.contentSecondary,
          marginBottom: "0px",
        })}
      >
        GraphQL Type: {field.graphQLType}
      </Paragraph>
      {field.notes && <Paragraph>{field.notes}</Paragraph>}
    </div>
  ));
};
